import React, { useMemo, useCallback, useState } from 'react';
import { Tabs, Tab, TabProps } from '@mui/material';
import { useIntl } from 'react-intl';
import makeStyles from '@mui/styles/makeStyles';
import { ClassNameMap } from '@mui/styles';
import AllTabIcon from '@mui/icons-material/List';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import AcceptedIcon from '@mui/icons-material/ThumbUp';
import TrashIcon from '@mui/icons-material/Delete';
import PrivateIcon from '@mui/icons-material/HideSourceSharp';
import { useNotesSelectionReset } from 'modules/notes/list/hooks';
import {
  NotesClassification,
  useNotesFilterContext,
  NotesFilterContextValue
} from './NotesFilterContextProvider';
import { useTimeSignalsCount } from './hooks/useTimeSignalsCount';

const icons = {
  all: <AllTabIcon />,
  inbox: <InboxIcon />,
  accepted: <AcceptedIcon />,
  private: <PrivateIcon />,
  trash: <TrashIcon />
};
type MyWorkTabsProps = {
  classes?: ClassNameMap;
  isDeleteAllTrashClicked?: boolean;
};

const useTabsStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },
  tab: {
    minWidth: theme.spacing(18),
    fontWeight: 600
  },
  indicatorHeight: {
    height: theme.spacing(0.3)
  }
}));

export const MyWorkTabs: React.FC<MyWorkTabsProps> = ({
  classes: overrideClasses,
  isDeleteAllTrashClicked
}: MyWorkTabsProps) => {
  const intl = useIntl();
  const tabsClasses = useTabsStyles({ classes: overrideClasses });
  const { loading, countByType, refetch } = useTimeSignalsCount();

  const getTabTitle = useMemo(
    () => (type: NotesClassification): string => {
      const count = !loading ? countByType(type) : 0;
      const computedCount = count && count > 0 ? `(${count})` : '';

      const tabsTitles = {
        All: intl.formatMessage({ id: 'notes.all' }),
        Unclassified: intl.formatMessage({ id: 'notes.inbox' }),
        Private: intl.formatMessage({ id: 'notes.private' }),
        Accepted: intl.formatMessage({ id: 'notes.accepted' }),
        Trash: intl.formatMessage({ id: 'notes.trash' })
      };

      switch (type) {
        case 'All':
          return `${tabsTitles.All} ${computedCount}`;
        case 'Unclassified':
          return `${tabsTitles.Unclassified} ${computedCount}`;
        case 'Accepted':
          return `${tabsTitles.Accepted} ${computedCount}`;
        case 'Private':
          return `${tabsTitles.Private} ${computedCount}`;
        case 'Trash':
          return !isDeleteAllTrashClicked
            ? `${tabsTitles.Trash} ${computedCount}`
            : `${tabsTitles.Trash}`;
      }
    },
    [countByType, intl, isDeleteAllTrashClicked, loading]
  );

  const {
    filterContext: { selectedClassification },
    setFilterContext
  } = useNotesFilterContext();

  const { clearForm } = useNotesSelectionReset();
  const [value, setValue] = useState<NotesClassification>(
    selectedClassification
  );

  const onClick = useCallback(
    (newSelection: NotesClassification) => {
      if (selectedClassification !== newSelection) {
        setFilterContext((previous: NotesFilterContextValue) => ({
          ...previous,
          ...{
            selectedClassification: newSelection
          }
        }));
        clearForm();
        refetch();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedClassification, setFilterContext]
  );
  const handleChange = useMemo(
    () => (event: React.SyntheticEvent, newValue: string) => {
      const selectedTab = newValue as NotesClassification;
      setValue(selectedTab);
      onClick(selectedTab);
    },
    [onClick]
  );

  const tabProps = useCallback(
    (type: NotesClassification): TabProps => ({
      value: type,
      label: getTabTitle(type)
    }),
    [getTabTitle]
  );

  return (
    <MyTabs
      value={value}
      handleChange={handleChange}
      tabProps={tabProps}
      tabsClasses={tabsClasses}
    />
  );
};

export default MyWorkTabs;

type MyTabsProps = {
  value: string;
  tabsClasses: ClassNameMap;
  handleChange: (event: React.SyntheticEvent, newValue: string) => void;
  tabProps: (type: NotesClassification) => TabProps;
};

const MyTabsFC: React.FC<MyTabsProps> = ({
  value,
  tabsClasses,
  tabProps,
  handleChange
}: MyTabsProps) => {
  return (
    <Tabs
      classes={tabsClasses}
      TabIndicatorProps={{
        className: tabsClasses.indicatorHeight
      }}
      value={value}
      onChange={handleChange}
      variant="scrollable"
      scrollButtons="auto"
      data-testid="my-work-tabs"
    >
      <Tab
        className={tabsClasses.tab}
        icon={icons.all}
        {...tabProps('All')}
        data-testid="tab-all"
      />
      <Tab
        className={tabsClasses.tab}
        icon={icons.inbox}
        {...tabProps('Unclassified')}
        data-testid="tab-inbox"
      />
      <Tab
        className={tabsClasses.tab}
        icon={icons.private}
        {...tabProps('Private')}
        data-testid="tab-private"
      />
      <Tab
        className={tabsClasses.tab}
        icon={icons.accepted}
        {...tabProps('Accepted')}
        data-testid="tab-accepted"
      />
      <Tab
        className={tabsClasses.tab}
        icon={icons.trash}
        {...tabProps('Trash')}
        data-testid="tab-trash"
      />
    </Tabs>
  );
};

export const MyTabs = React.memo(MyTabsFC);
